// src/components/admin/diagnostics/DiagnosticContentTools.js
//
// Secondary "Advanced Tools" page that consolidates the SAT, PSAT, and ACT
// content-management tools (test creation, question uploads, scoring tables)
// behind a single tabbed page. These are developer/admin tools and are kept out
// of the prominent sidebar. Each tab renders the existing management component
// unchanged — all upload, scoring, and content functionality is preserved.

import React, { useState } from "react";
import DSATManagement from "../dsat/DSATManagement";
import PSATManagement from "../psat/PSATManagement";
import ACTManagement from "../act/ACTManagement";

const TABS = [
  { key: "sat", label: "SAT Content", Component: DSATManagement },
  { key: "psat", label: "PSAT Content", Component: PSATManagement },
  { key: "act", label: "ACT Content", Component: ACTManagement },
];

const C = { ink: "#111827", sub: "#6b7280", line: "#e5e7eb" };

const styles = {
  page: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "#fff",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  },
  bar: {
    flexShrink: 0,
    borderBottom: `1px solid ${C.line}`,
    padding: "14px 24px 0",
    background: "#fff",
  },
  heading: { margin: 0, fontSize: 18, fontWeight: 800, color: C.ink, letterSpacing: "-0.3px" },
  caption: { margin: "3px 0 12px", fontSize: 12, color: C.sub },
  tabs: { display: "flex", gap: 4 },
  tab: (active) => ({
    appearance: "none",
    border: "none",
    background: "transparent",
    padding: "9px 14px",
    fontSize: 13,
    fontWeight: 600,
    color: active ? C.ink : C.sub,
    borderBottom: active ? "2px solid #111827" : "2px solid transparent",
    cursor: "pointer",
    marginBottom: -1,
  }),
  body: { flex: 1, minHeight: 0, overflow: "auto" },
};

export default function DiagnosticContentTools() {
  const [active, setActive] = useState("sat");
  const ActiveComponent = TABS.find((t) => t.key === active)?.Component || DSATManagement;

  return (
    <div style={styles.page}>
      <div style={styles.bar}>
        <h2 style={styles.heading}>Content &amp; Tools</h2>
        <p style={styles.caption}>
          Advanced tools for building and updating diagnostic tests. Create tests, upload questions,
          and manage scoring tables.
        </p>
        <div style={styles.tabs} role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={active === t.key}
              style={styles.tab(active === t.key)}
              onClick={() => setActive(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div style={styles.body} key={active}>
        <ActiveComponent />
      </div>
    </div>
  );
}
